import {
	type FC,
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

import { useResponsive } from "~/components/canvas/hooks/useResponsive";
import { notImplementedFn } from "~/utils/context";
import { LOCAL_STORAGE_KEYS, PANE_IDS, getItemById, setItemById } from "~/utils/storage";

import { useEasel } from "../easel";
import { useCurrentDocument } from "../hooks/useCurrentDocument";

type NavigationPaneState = {
	isReadOnly: boolean;
	canDelete: boolean;
	renamingPageID: string | undefined;
	setRenamingPageID: (id: string) => void;
	unsetRenamingPageID: () => void;
	isRenamingCanvas: boolean;
	setIsRenamingCanvas: () => void;
	unsetIsRenamingCanvas: () => void;
	isExpanded: boolean;
	toggleExpanded: () => void;
};

const NavigationPaneContext = createContext<NavigationPaneState>({
	isReadOnly: false,
	canDelete: false,
	renamingPageID: undefined,
	setRenamingPageID: notImplementedFn("setRenamingPageID", "NavigationPaneContext", undefined),
	unsetRenamingPageID: notImplementedFn(
		"unsetRenamingPageID",
		"NavigationPaneContext",
		undefined
	),
	isRenamingCanvas: false,
	setIsRenamingCanvas: notImplementedFn(
		"setIsRenamingCanvas",
		"NavigationPaneContext",
		undefined
	),
	unsetIsRenamingCanvas: notImplementedFn(
		"unsetIsRenamingCanvas",
		"NavigationPaneContext",
		undefined
	),
	isExpanded: true,
	toggleExpanded: notImplementedFn("toggleExpanded", "NavigationPaneContext", undefined),
});

export const useNavigationPane = () => useContext(NavigationPaneContext);

export type NavigationPaneContextProviderProps = {
	isReadOnly?: boolean;
	canDelete?: boolean;
	children: ReactNode;
};

export const NavigationPaneContextProvider: FC<NavigationPaneContextProviderProps> = ({
	canDelete = false,
	children,
}) => {
	const [renamingPageIDState, setRenamingPageIDState] = useState<string | undefined>(undefined);
	const { isMd } = useResponsive();

	const currentDocument = useCurrentDocument();
	const { canvasWritable } = useEasel();

	const canvasID = useMemo(() => currentDocument?.id ?? "unknown", [currentDocument]);
	const storageKey = useMemo(() => `${PANE_IDS.NAV_PANE}-${canvasID}`, [canvasID]);

	const existingState = getItemById<boolean>(LOCAL_STORAGE_KEYS.PANES, storageKey);
	const defaultState = existingState?.value ?? true;
	const responsiveDefaultState = isMd ? defaultState : false;
	const [isExpanded, setIsExpanded] = useState<boolean>(responsiveDefaultState);

	const toggleExpanded = useCallback(() => {
		setIsExpanded((s) => {
			if (isMd) {
				// Only persist state this on Md screens and above
				setItemById<boolean>(LOCAL_STORAGE_KEYS.PANES, storageKey, !s, 0);
			}

			return !s;
		});
	}, [isMd, storageKey]);

	const setRenamingPageID = useCallback(
		(id: string) => {
			if (!canvasWritable) {
				return;
			}
			setRenamingPageIDState(id);
		},
		[canvasWritable]
	);

	const unsetRenamingPageID = useCallback(() => {
		if (!canvasWritable) {
			return;
		}
		setRenamingPageIDState(undefined);
	}, [canvasWritable]);

	const renamingPageID = canvasWritable ? renamingPageIDState : undefined;

	const [isRenamingCanvasState, setIsRenamingCanvasState] = useState<boolean>(false);

	const setIsRenamingCanvas = useCallback(() => {
		if (!canvasWritable) {
			return;
		}
		setIsRenamingCanvasState(true);
	}, [canvasWritable]);

	const unsetIsRenamingCanvas = useCallback(() => {
		if (!canvasWritable) {
			return;
		}
		setIsRenamingCanvasState(false);
	}, [canvasWritable]);

	const isRenamingCanvas = canvasWritable ? isRenamingCanvasState : false;

	const state = {
		isReadOnly: !canvasWritable,
		canDelete,
		renamingPageID,
		setRenamingPageID,
		unsetRenamingPageID,
		isRenamingCanvas,
		setIsRenamingCanvas,
		unsetIsRenamingCanvas,
		isExpanded,
		toggleExpanded,
	};

	return (
		<NavigationPaneContext.Provider value={state}>{children}</NavigationPaneContext.Provider>
	);
};
